import React, {useEffect, useState} from "react";
import styled from "styled-components";
import axios from "axios";
import FairInfoBar from "../FairInfoBar";
import { useNavigate } from "react-router-dom";
import { RedButton } from "../Button";
import Heading from "../Heading";
import { handleGenericErrors, handleGenericSuccess } from "../../utils/ErrorUtility";
import LeftRail from "./LeftRail/LeftRail";
import SaveChangesModal from "./SaveChangesModal";
import CurrencyInput from "../CurrencyInput";
import { trackFinFormPageChange, trackFinFormSaveAndContinueButton, updateUserCashEntry, updateUserChequesEntry, updateUserFairId, updateUserManualImprinter, updateUserWirelessTransaction } from "../../DumbleData";

interface Props {
	language: string;
}

export const FinFormSalesPage = ( {language} : Props ) => {
    const [fairInfo, setFairInfo] = useState<any>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [cash, setCash] = useState<string>("0.00");
    const [cheques, setCheques] = useState<string>("0.00");
    const [manualTransactions, setManualTransactions] = useState<string>("0.00");
    const [wirelessTransactions, setWirelessTransactions] = useState<string>("0.00");
    const [latestStep, setLatestStep] = useState<string>("");

    const [nextLocation, setNextLocation] = useState("/financial-form/sales-summary")
    const [changesMade, setChangesMade] = useState<boolean>(false);
    const [saveChangesModalOpen, setSaveChangesModalOpen] = useState<boolean>(false);
    const navigate = useNavigate();

    const formatCurrency = (currency: String) => {
        let output = currency.replace(/\D/g, "");
        output = output.replace(/^0+(?=\d)/, "");

        // accounting for ctrl + a behavior
        // Limit user input to min $0.00
        if (output.length === 2) {
            output = "0" + output;
        } else if (output.length === 1) {
            output = "00" + output;
        } else if (output.length === 0) {
            output = "000";
        }

        output = output.substring(0,output.length-2) + "." + output.substring(output.length-2);
        setChangesMade(true);
        return output;
    };
    const handleCash = (currency: String) => {
        if (currency.length < 11) {
            setCash(formatCurrency(currency));
        }
    }
    const handleCheques = (currency: String) => {
        if (currency.length < 11) {
            setCheques(formatCurrency(currency));
        }
    }
    const handleManualTransactions = (currency: String) => {
        if (currency.length < 11) {
            setManualTransactions(formatCurrency(currency));
        }
    }
    const handleWirelessTransactions = (currency: String) => {
        if (currency.length < 11) {
            setWirelessTransactions(formatCurrency(currency));
        }
    }

    // put request
    const handleSales = async () => {
        trackFinFormSaveAndContinueButton();
        try {
            const sales = {
                cash: cash,
                cheques: cheques,
                manualTransactions: manualTransactions,
                wirelessTransactions: wirelessTransactions
            }
            cash !== "0.00" ? updateUserCashEntry(`TRUE | ${cash}`) : updateUserCashEntry("FALSE");
            cheques !== "0.00" ? updateUserChequesEntry(`TRUE | ${cheques}`) : updateUserChequesEntry("FALSE");
            manualTransactions !== "0.00" ? updateUserManualImprinter(`TRUE | ${manualTransactions}`) : updateUserManualImprinter("FALSE");
            wirelessTransactions !== "0.00" ? updateUserWirelessTransaction(`TRUE | ${wirelessTransactions}`) : updateUserWirelessTransaction("FALSE");
            await axios.put(
                `${process.env.REACT_APP_ENV_BASE_URL}/api/user/fairs/current/financials/form/sales`,
                {
                    sales
                },
                    {
                        withCredentials: true,
                    }
            )
            .then((response) => {
                if (response.status === 200) {
                    navigate(nextLocation);
                }
                else {
                    handleGenericSuccess(response);
                }
            })
            .catch((err) => {
                handleGenericErrors(err);
            });
        } catch (err: any) {
            handleGenericErrors(err);
        }
    };

    // GET Request
    useEffect(() => {
        const getFinancialFormSales = async () => {
            try {
                await axios.get(
                    `${process.env.REACT_APP_ENV_BASE_URL}/api/user/fairs/current/financials/form/sales`,
                        {
                            withCredentials: true,
                        }
                )
                .then((response) => {
                    if (response.status === 200) {
                        if (!response.data.isFinancialFormAvailable) {
                            navigate("/financial-form/intro");
                        };
                        if (response.data.latestStep === "CLOSED") {
                            navigate("/financial-form/confirmation");
                        }
                        updateUserFairId(response.data.fair.id);

                        const fairInfo = response.data.fair;
                        fairInfo.start = response.data.fair.startDate;
                        fairInfo.end = response.data.fair.endDate;
                        setFairInfo(fairInfo);
                        setLatestStep(response.data.latestStep);

                        const sales = response.data.sales;
                        setCash(sales.cash.toFixed(2));
                        setCheques(sales.cheques.toFixed(2));
                        setManualTransactions(sales.manualTransactions.toFixed(2));
                        setWirelessTransactions(sales.wirelessTransactions.toFixed(2));
                    }
                    else {
                        handleGenericSuccess(response);
                    }
                })
                .catch((err) => {
                    handleGenericErrors(err);
                });
            } catch (err: any) {
                handleGenericErrors(err);
            } finally {
                setIsLoading(false);
                trackFinFormPageChange("Financial Form Sales Page");
            }
        };
        getFinancialFormSales();
    }, [navigate]);

    // handling left rail clicks
    const handleNextLocation = (leftRailLocation: string) => {
        if(changesMade) {
            setNextLocation(leftRailLocation);
            setSaveChangesModalOpen(true);
        } else {
            navigate(leftRailLocation);
        }
    }

    // updating nextLocation when save changes modal closed
    useEffect(() => {
        if(!saveChangesModalOpen) {
            setNextLocation("/financial-form/sales-summary");
        }
    }, [saveChangesModalOpen])

    return (<>
        {!isLoading && (<>
            <FairInfoBar
                language={language}
                enableSwitch={fairInfo.enableSwitch}
                fairInfoData={fairInfo}
                organizationData={fairInfo}
                pageType={"financial-form"}
                />
            <LeftRail
                language={language}
                latestStep={latestStep}
                handleNextLocation={handleNextLocation}
            />
            <StyledDiv>
                <StyledHeading>{language === "en" ? "Sales" : "Ventes"}</StyledHeading>
                <StyledCard>
                    <SubHeading>
                        {language === "en" ? "Rewards Worksheet" : "Informations sur le formulaire"}
                        <br />
                        {language === "en" ? "Order Information" : "de récompenses"}
                    </SubHeading>
                    <StyledTextInput
                        heading={language === "en" ? "Interac/Debit and Credit" : "Débit/Interac et crédit"}
                        label={language === "en" ? "(Total the amounts for all credit cards and Interac from ALL End of Day Reports)" : "(Faites le total des montants de toutes les cartes de crédit et de débit/Interac de TOUS les rapports de fin de journée.)"}
                        value={wirelessTransactions}
                        language={language}
                        onChange={handleWirelessTransactions}
                    ></StyledTextInput>
                    <StyledTextInput
                        heading={language === "en" ? "Cash" : "Argent comptant"}
                        value={cash}
                        language={language}
                        onChange={handleCash}
                    ></StyledTextInput>
                    <StyledTextInput
                        heading={language === "en" ? "Cheques" : "Chèques"}
                        value={cheques}
                        language={language}
                        onChange={handleCheques}
                    ></StyledTextInput>
                    <StyledTextInput
                        heading={language === "en" ? "Manual imprinter transactions" : "Transactions manuelles par imprimante"}
                        label={language === "en" ? "(If applicable, include imprinter slips with payment)" : "(le cas échéant, joignez les bordereaux d'impression au paiement.)"}
                        value={manualTransactions}
                        language={language}
                        onChange={handleManualTransactions}
                    ></StyledTextInput>
                </StyledCard>
                <StyledRedButton
                    handleClick={handleSales}
                >{language === "en" ? "SAVE & CONTINUE" : "Sauvegarder et continuer"}</StyledRedButton>
            </StyledDiv>
            <SaveChangesModal 
                language={language}
                saveAndPublish={handleSales}
                setModalIsOpen={setSaveChangesModalOpen}
                modalIsOpen={saveChangesModalOpen}
            />
        </>)}
    </>);
};

export default FinFormSalesPage;

const StyledDiv = styled.div`
    text-align: right;
    max-width: 600px;
    margin: 0px auto;
    padding: 40px 40px 120px 40px;
    font-family: museo-sans;
    font-size: 39px;
    font-weight: 300;
    line-height: 42px;
    height: auto;
    @media (max-width: 1080px) {
        padding-top: 90px;
    }
`;

const StyledHeading = styled(Heading)`
    text-align: left;
    max-width: 600px;
    margin: 0px auto;
    padding: 30px 0px;
    font-family: museo-sans
    font-size: 39px;
    font-weight: 300;
    line-height: 42px;
`;

const StyledTextInput = styled(CurrencyInput)`
    border-top: 1px solid #D7D7D7;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0px;
     label {
         border: none;
     }
    input {
        border: 1px solid #919191;
        max-width: 120px;
        padding: 16px;
    }
    @media (max-width: 519px) {
        flex-direction: column;
        align-items: start;
    }
    @media (min-width: 519px) {
        label {
            padding-right: 16px;
        }
    }
`;

const SubHeading = styled(Heading)`
    text-align: left;
    font-family: museo-sans;
    font-size: 28px;
    font-weight: 300;
    line-height: 32px;
    text-align: left;
    padding-bottom: 24px;
`;

const StyledCard = styled.div`
    text-align: left;
    max-width: 600px;
    background-color: #F5F5F5;
    margin: 0px auto;
    padding: 48px 80px;
    box-sizing: border-box;
    border-radius: 16px;
    @media (max-width: 519px) {
        padding: 48px 25px;
    }
`;

const StyledRedButton = styled(RedButton)`
    height: 40px;
    margin-top: 34px;
    font-family: museo-sans;
    font-size: 11px;
    font-weight: 600;
    line-height: 12px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
`;